/**
 * boardIdentityStore.ts — board header identity, fetched early and remembered.
 *
 * The board header (org name + dial-in number) used to be fetched inside
 * BoardScreen's mount effect, which put it last in a serial chain: probe
 * /v1/me → render the console → user opens Dashboard → THEN ask for the
 * number. Until it landed the header showed "—", which reads as "broken",
 * not "loading".
 *
 * This module fixes the two halves of that:
 *   • PREFETCH — the console kicks the read off as soon as the session
 *     resolves, so it's in flight (or already done) before Dashboard mounts.
 *   • REMEMBER — the last good answer is persisted per-org, so a reload
 *     paints the real number on the first frame and the network read merely
 *     confirms it.
 *
 * Identity is cookie-scoped and effectively static (name/number/vertical),
 * so a stale-while-revalidate cache is safe. A FAILED read is never cached
 * and never overwrites a good value — a blip must not blank the header.
 */

import { useEffect, useSyncExternalStore } from 'react';

import { getBoardIdentity, BoardIdentity } from './voiceService';

const CACHE_KEY = 'sk_board_identity';

/** Retry backoff for a failed identity read. The old code swallowed the error
 *  and left the header at "—" forever; one transient failure was permanent. */
const RETRY_DELAYS_MS = [600, 2_000, 5_000];

interface CacheEntry {
  orgId: string;
  identity: BoardIdentity;
}

type Listener = () => void;

let current: BoardIdentity | null = null;
let currentOrgId: string | null = null;
/** Which org we've already tried the localStorage seed for (distinct from
 *  `currentOrgId`, which only tracks orgs we actually hold an identity for). */
let seededOrgId: string | null = null;
let inflight: Promise<void> | null = null;
const listeners = new Set<Listener>();

function emit(): void {
  for (const l of listeners) l();
}

// Persistence is best-effort: private-mode Safari, disabled storage and native
// (no localStorage) all just mean "no instant paint", never a crash.
function readCache(orgId: string): BoardIdentity | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw) as CacheEntry;
    // Guard the org: showing the previous tenant's dial-in after an org switch
    // would be worse than showing nothing.
    return entry?.orgId === orgId ? entry.identity : null;
  } catch {
    return null;
  }
}

function writeCache(orgId: string, identity: BoardIdentity): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(CACHE_KEY, JSON.stringify({ orgId, identity } satisfies CacheEntry));
  } catch {
    /* quota / disabled storage — the in-memory value still serves this session */
  }
}

const sleep = (ms: number) => new Promise<void>((res) => { setTimeout(res, ms); });

async function fetchWithRetry(orgId: string): Promise<void> {
  // One attempt per delay, plus the initial try. getBoardIdentity resolves with
  // `live: false` (not a rejection) when the read fails, so retry on that flag.
  for (let attempt = 0; ; attempt++) {
    const res = await getBoardIdentity();
    if (res.live) {
      current = res.data;
      currentOrgId = orgId;
      writeCache(orgId, res.data);
      emit();
      return;
    }
    if (attempt >= RETRY_DELAYS_MS.length) return; // keep any cached value
    await sleep(RETRY_DELAYS_MS[attempt]);
  }
}

/**
 * Start (or reuse) the identity read for this org. Safe to call repeatedly —
 * concurrent callers share one request. Seeds from cache synchronously so a
 * subsequent read of the store already has a value to show.
 */
export function prefetchBoardIdentity(orgId: string | null): void {
  if (!orgId) return;

  if (orgId !== seededOrgId) {
    // New org: never show the previous tenant's number while the new one loads.
    // Seeding from cache here is what makes a reload paint on the first frame.
    seededOrgId = orgId;
    current = readCache(orgId);
    currentOrgId = current ? orgId : null;
    inflight = null;
    emit();
  }

  if (inflight) return;
  inflight = fetchWithRetry(orgId).finally(() => { inflight = null; });
}

export function subscribeBoardIdentity(listener: Listener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

// `current` is only ever replaced with a whole new object, so it is a stable
// snapshot reference between emits — which is what useSyncExternalStore needs
// to avoid re-rendering forever.
function getSnapshot(): BoardIdentity | null {
  return current;
}

/**
 * Read the board identity, kicking off the fetch if nobody has yet. Returns the
 * cached value immediately when there is one, so the header renders the real
 * number on the first frame instead of "—".
 */
export function useBoardIdentity(orgId: string | null): BoardIdentity | null {
  useEffect(() => { prefetchBoardIdentity(orgId); }, [orgId]);
  const identity = useSyncExternalStore(subscribeBoardIdentity, getSnapshot, getSnapshot);
  return currentOrgId === orgId ? identity : null;
}

/** Test seam — drops in-memory state so suites don't leak identity across cases. */
export function __resetBoardIdentityForTests(): void {
  current = null;
  currentOrgId = null;
  seededOrgId = null;
  inflight = null;
  listeners.clear();
}
